/*
 * Instant task reporting (v1 slice) — a collapsible card next to Task
 * Generation on the sales task list.
 *
 * Two plain tables, by rule and by rep, over a date range in the header timezone. The PRD wants
 * "enough to spot a rep or a rule that isn't converting", not a dashboard, so
 * there are no charts and no trends.
 *
 * SUPER_USER only (product decision Q14). The gate is the `can_view` prop, which
 * the page passes from the SAME `can_edit` boolean the generation config GET
 * returns — one server-derived capability rather than a second client-side role
 * check that could drift from it. Like every capability boolean here it only
 * decides what renders; the endpoint enforces access itself.
 *
 * The range is the report's own, deliberately not the list's due-date filter:
 * this report counts by created/claimed date, so sharing the filter would make
 * the two disagree while looking like one control. It uses the same
 * DateRangeFilter component.
 */

import React, { Component } from 'react';
import { FaChevronDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa';
import DateRangeFilter from '../early_retention_checklist/DateRangeFilter';
import StackTd from './StackTable';
import { stackClasses } from './stack_table';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import {
  autoCompletedRate,
  defaultReportRange,
  dismissRate,
  formatRate,
  formatSeconds,
  isReportEmpty,
  normalizeReport,
  repTotals,
  reportTiles,
  reportUrlPath,
  ruleTotals
} from './instant_report';
import type { InstantReportData, ReportRange } from './instant_report';
import { filterTzAbbr, resolveFilterTz } from './filter_tz';
import { LABEL_CLASS, btnStyle } from './ui';

interface Props {
  // SUPER_USER, as reported by the generation config endpoint's can_edit.
  can_view?: boolean;
  // When set, the parent owns expansion (the page's tab) and the header is static.
  open?: boolean;
  // Header timezone (IANA) the date range is in; unset = IST, as the server reads it.
  tz?: string;
}

interface State {
  collapsed: boolean;
  loading: boolean;
  // Set on a failed fetch and cleared on the next one. Held as a message
  // rather than a boolean so the server's own reason reaches the operator.
  error: string;
  // Whether a response has landed at least once, so the empty state is not
  // shown in place of the first load.
  loaded: boolean;
  report: InstantReportData;
  dateFrom: string;
  dateTo: string;
}

interface ReportError {
  response?: { data?: { reason?: string } };
}

const EMPTY_REPORT: InstantReportData = { by_rule: [], by_rep: [] };

// The endpoint needs both bounds (an unbounded report would scan the table), so
// the range state is never empty: "clear" resets to the default window rather
// than to "any date" the way the list's own filter does.
const rangeState = (tz?: string): Pick<State, 'dateFrom' | 'dateTo'> => {
  const { from, to } = defaultReportRange(tz);
  return { dateFrom: from, dateTo: to };
};

const isCollapsed = (props: Props, state: State): boolean =>
  props.open === undefined ? state.collapsed : !props.open;

const cellClass = 'px-3.5 py-2 text-right tabular-nums';
const headClass = `px-3.5 py-2 text-right ${LABEL_CLASS}`;
const firstHead = `w-[30%] px-3.5 py-2 text-left ${LABEL_CLASS}`;
const ST = stackClasses();
// Short headers so six columns fit beside the rep table; card mode reuses them as captions.
const RULE_COLS = ['Created', 'Opened', 'Completed', 'Auto-completed', 'Median time to open'];
const REP_COLS = ['Opened', 'Completed', 'Alerts shown', 'Alerts dismissed', 'Dismiss rate'];
const ROW = `border-t border-line-subtle text-[12.5px] ${ST.row}`;
const TOTAL_ROW = `border-t border-line-subtle bg-surface-sunken text-[12.5px] font-semibold ${ST.row}`;

// Numeric cells of one row, labelled for card mode from the column list.
const numCells = (cols: string[], values: React.ReactNode[]) =>
  values.map((v, i) => (
    <StackTd key={cols[i]} label={cols[i]} className={cellClass}>
      {v}
    </StackTd>
  ));

const headRow = (first: string, cols: string[]) => (
  <thead className={`bg-surface-sunken ${ST.head}`}>
    <tr className="border-b border-line-subtle">
      <th className={firstHead}>{first}</th>
      {cols.map((c) => (
        <th key={c} className={headClass}>
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

class InstantReport extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      collapsed: true,
      loading: false,
      error: '',
      loaded: false,
      report: EMPTY_REPORT,
      ...rangeState(props.tz)
    };
  }

  componentDidMount() {
    const { can_view: canView = false } = this.props;
    if (canView && !isCollapsed(this.props, this.state)) this.loadReport();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Fetch lazily: the report is an aggregate query and most agents never open
    // the card. First expansion loads it; after that only Refresh, a range
    // change and a header-timezone change do.
    const { loaded } = this.state;
    const { can_view: canView = false, tz } = this.props;
    const opened = !isCollapsed(this.props, this.state) && isCollapsed(prevProps, prevState);
    if (canView && opened && !loaded) {
      this.loadReport();
      return;
    }
    // Same calendar days, re-read in the new zone; only once it has been loaded.
    if (canView && loaded && prevProps.tz !== tz) this.loadReport();
  }

  setRange = ({ from, to }: ReportRange) => {
    this.setState({ dateFrom: from, dateTo: to }, this.loadReport);
  };

  resetRange = () => {
    const { tz } = this.props;
    this.setState(rangeState(tz), this.loadReport);
  };

  loadReport = () => {
    const { dateFrom, dateTo } = this.state;
    const { tz } = this.props;
    if (!dateFrom || !dateTo) return;
    this.setState({ loading: true, error: '' });
    const url = getApiUrl(reportUrlPath({ date_from: dateFrom, date_to: dateTo, tz }));
    getApi(url, true)
      .then((res: { data?: { data?: Parameters<typeof normalizeReport>[0] } }) => {
        this.setState({
          report: normalizeReport(res?.data?.data),
          loading: false,
          loaded: true
        });
      })
      .catch((e: ReportError) => {
        this.setState({
          loading: false,
          loaded: true,
          report: EMPTY_REPORT,
          error: e?.response?.data?.reason || 'Could not load the instant task report'
        });
      });
  };

  renderRuleTable = () => {
    const {
      report: { by_rule: byRule }
    } = this.state;
    const totals = ruleTotals(byRule);
    return (
      <table className={ST.table}>
        {headRow('Rule', RULE_COLS)}
        <tbody className={ST.body}>
          {byRule.map((row) => (
            <tr key={row.task_type} className={ROW}>
              <StackTd
                className="truncate px-3.5 py-2 text-left font-medium md:font-normal"
                title={row.task_label}
              >
                {row.task_label}
              </StackTd>
              {numCells(RULE_COLS, [
                row.created,
                row.opened,
                row.completed,
                row.auto_completed,
                formatSeconds(row.median_time_to_open_seconds)
              ])}
            </tr>
          ))}
        </tbody>
        <tfoot className={ST.body}>
          <tr className={TOTAL_ROW}>
            <StackTd className="px-3.5 py-2 text-left">
              All rules
              <span className="ml-2 text-xs font-normal text-content-muted">
                {formatRate(autoCompletedRate(totals))} expired unworked
              </span>
            </StackTd>
            {numCells(RULE_COLS, [
              totals.created,
              totals.opened,
              totals.completed,
              totals.auto_completed,
              '—'
            ])}
          </tr>
        </tfoot>
      </table>
    );
  };

  /*
   * The rep cut carries no Created or Auto-completed column: a task is created
   * before anyone holds it and expires without anyone holding it, so neither is
   * attributable to a rep, and the endpoint sends neither. The pop-up pair takes
   * their place — dismissed/shown is the PRD's pop-up fatigue metric.
   */
  renderRepTable = () => {
    const {
      report: { by_rep: byRep }
    } = this.state;
    const totals = repTotals(byRep);
    return (
      <table className={ST.table}>
        {headRow('Rep', REP_COLS)}
        <tbody className={ST.body}>
          {byRep.map((row) => (
            <tr key={row.ops_uuid} className={ROW}>
              <StackTd
                className="truncate px-3.5 py-2 text-left font-medium capitalize md:font-normal"
                title={row.name}
              >
                {row.name}
              </StackTd>
              {numCells(REP_COLS, [
                row.opened,
                row.completed,
                row.alerts_shown,
                row.alerts_dismissed,
                formatRate(dismissRate(row))
              ])}
            </tr>
          ))}
        </tbody>
        <tfoot className={ST.body}>
          <tr className={TOTAL_ROW}>
            <StackTd className="px-3.5 py-2 text-left">All reps</StackTd>
            {numCells(REP_COLS, [
              totals.opened,
              totals.completed,
              totals.alerts_shown,
              totals.alerts_dismissed,
              formatRate(dismissRate(totals))
            ])}
          </tr>
        </tfoot>
      </table>
    );
  };

  renderBody = () => {
    const { loading, error, loaded, report } = this.state;
    if (loading && !loaded) {
      return (
        <div className="px-5 py-10 text-center text-sm text-content-muted">
          Loading instant task report…
        </div>
      );
    }
    if (error) {
      return (
        <div className="px-5 py-8 text-center text-sm" style={{ color: 'var(--state-error)' }}>
          {error}
        </div>
      );
    }
    if (loaded && isReportEmpty(report)) {
      return (
        <div className="px-5 py-8 text-center text-sm text-content-muted">
          No instant tasks in this date range.
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3.5 px-5 pb-5">
        <div
          data-testid="instant-report-tiles"
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
        >
          {reportTiles(report).map((t) => (
            <div key={t.key} className="rounded-[10px] border border-line-subtle px-3 py-2">
              <div className="text-[11px] text-content-muted">{t.label}</div>
              <div className="text-lg font-bold tabular-nums text-content-primary">{t.value}</div>
              <div className="text-[11px] text-content-muted">{t.sub}</div>
            </div>
          ))}
        </div>
        <div className="grid items-start gap-3.5 xl:grid-cols-[1.25fr_1fr]">
          <section className="overflow-hidden rounded-[10px] border border-line-subtle">
            <h3 className={`m-0 border-b border-line-subtle px-3.5 py-2 ${LABEL_CLASS}`}>
              By rule
            </h3>
            {this.renderRuleTable()}
          </section>
          <section className="overflow-hidden rounded-[10px] border border-line-subtle">
            <h3 className={`m-0 border-b border-line-subtle px-3.5 py-2 ${LABEL_CLASS}`}>By rep</h3>
            {this.renderRepTable()}
          </section>
        </div>
      </div>
    );
  };

  render() {
    const { can_view: canView = false } = this.props;
    // Q14: SUPER_USER only. Unlike the generation card there is no read-only
    // mode — a report nobody may act on is not worth the aggregate query.
    if (!canView) return null;

    const { loading, dateFrom, dateTo } = this.state;
    const { open, tz } = this.props;
    const collapsed = isCollapsed(this.props, this.state);
    const Chevron = collapsed ? FaChevronRight : FaChevronDown;
    const title = (
      <>
        <h2 className="m-0 text-lg font-semibold text-content-primary">Instant task report</h2>
        <span className="text-xs text-content-muted">
          Instant tasks created, opened, completed and auto-completed, by rule and by rep.
        </span>
      </>
    );
    return (
      <section
        data-testid="instant-report"
        className="rounded-[10px] border border-line-subtle bg-surface-card text-[13px] text-content-primary"
        style={{ margin: '20px 5%' }}
      >
        {open !== undefined ? (
          <div className="flex w-full flex-wrap items-center gap-x-2.5 px-5 py-3.5">{title}</div>
        ) : (
          <button
            type="button"
            onClick={() => this.setState((prev) => ({ collapsed: !prev.collapsed }))}
            aria-expanded={!collapsed}
            className="flex w-full flex-wrap items-center gap-x-2.5 px-5 py-3.5 text-left"
            // Explicit colour/margin override the host CMS's global `button {}`
            // rule, which would otherwise paint white text on a white card.
            style={{
              background: 'transparent',
              border: 'none',
              margin: 0,
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
          >
            <Chevron size={12} className="text-content-muted shrink-0" />
            {title}
          </button>
        )}
        {collapsed ? null : (
          <>
            <div className="flex flex-wrap items-center gap-2.5 px-5 pb-3.5">
              <DateRangeFilter
                from={dateFrom}
                to={dateTo}
                onChange={this.setRange}
                onClear={this.resetRange}
              />
              <span className="text-xs text-content-muted" title={resolveFilterTz(tz)}>
                Dates in {filterTzAbbr(tz)}, like every date on this page.
              </span>
              <button
                type="button"
                onClick={this.loadReport}
                disabled={loading}
                aria-label="Refresh instant task report"
                title="Refresh"
                className="ml-auto disabled:opacity-50"
                style={{
                  ...btnStyle('neutral', 'md'),
                  width: 36,
                  padding: 0,
                  borderRadius: 9999,
                  color: 'var(--text-link-brand)',
                  cursor: loading ? 'wait' : 'pointer'
                }}
              >
                <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
            {this.renderBody()}
          </>
        )}
      </section>
    );
  }
}

export default InstantReport;
